import React, { createContext, useContext, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import moment from 'moment'
import API from '../../../../services/API'

import Alert from '@mui/material/Alert'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'

import { DetailRow } from '../../../../components/Cards/DetailsBox'
import {
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { GlobalContext } from '../../../../App'
import Summary from './Summary'
import Description from './Description'
import Images from './Images'
import Vehicle from './Vehicle'
import LogsComponent from '../../../../components/Logs/LogsComponent'
import Asset from './Asset'
import AssignedTo from './AssignedTo'
import UnsavedChanges from '../../../../components/UnsavedChanges/UnsavedChanges'
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded'
import BuildCircleRounded from '@mui/icons-material/BuildCircleRounded'
import NotesRounded from '@mui/icons-material/NotesRounded'
import TuneRounded from '@mui/icons-material/TuneRounded'
import ForumOutlined from '@mui/icons-material/ForumOutlined'
import { getStatusColor } from '../../../../components/Kanban/KanbanTab'
import { Coronavirus } from '@mui/icons-material'
import { DEFECT_PRIORITIES, DEFECT_STATUSES } from '../statuses'

export const DefectContext = createContext()

const priorityLabels = {
  1: 'Low priority',
  2: 'Normal priority',
  3: 'High priority',
}

const SectionHeader = ({ icon, title, description }) => (
  <Stack direction="row" spacing={1.25} alignItems="center">
    <Avatar
      sx={{
        width: 38,
        height: 38,
        bgcolor: 'rgba(35, 154, 164, 0.12)',
        color: 'contrast.main',
      }}
    >
      {icon}
    </Avatar>
    <Box>
      <Typography variant="h6" fontSize="1rem">
        {title}
      </Typography>
      {description && (
        <Typography variant="body2" fontSize="0.78rem">
          {description}
        </Typography>
      )}
    </Box>
  </Stack>
)

const surfaceSx = {
  p: { xs: 2, sm: 2.5 },
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 2.5,
  boxShadow: '0 8px 28px rgba(15, 23, 42, 0.05)',
}

const SingleDefect = () => {
  const [loading, setLoading] = useState(true)
  const { defectId } = useParams()
  const [defect, setDefect] = useState({})
  const [fetchError, setFetchError] = useState(false)
  const [changesPresent, setChangesPresent] = useState(false)
  const [openComment, setOpenComment] = useState(false)
  const navigate = useNavigate()

  const [priorityAnchorEl, setPriorityAnchorEl] = useState(null)
  const [statusAnchorEl, setStatusAnchorEl] = useState(null)

  const loadDefect = () => {
    setLoading(true)
    setFetchError(false)
    API.get(`/defect/${defectId}`)
      .then((res) => {
        if (res.status === 200) {
          setDefect(res.data)
        } else {
          setFetchError(true)
        }
      })
      .catch((e) => setFetchError(true))
      .finally(() => setLoading(false))
  }

  useEffect(() => loadDefect(), [])

  const { setToast } = useContext(GlobalContext)

  const handleSave = () => {
    API.put(`/defect/${defect._id}`, {
      description: defect.description,
      priority: defect.priority,
      vehicle: defect.vehicle,
      asset: defect?.linkedAsset?.assetID ?? defect?.asset ?? null,
      isIPC: defect.isIPC,
      status: defect.status,
      assignedTo: defect.assignedTo,
      archived: defect.archived,
      summary: defect.summary,
    })
      .then((res) => {
        if (res.status === 200) {
          setToast({ message: 'Defect Updated' })
          setChangesPresent(false)
        }
      })
      .catch((e) => {
        setToast({
          message:
            e.response?.data === 'No Matching Asset'
              ? 'The selected asset could not be found'
              : 'Unable to save changes',
          severity: 'error',
        })
      })
  }

  const reporterName = [
    defect?.reportedBy?.firstName,
    defect?.reportedBy?.lastName,
  ]
    .filter(Boolean)
    .join(' ')

  const handlePriorityChange = (priority) => {
    setDefect((prev) => ({
      ...prev,
      priority,
    }))
    setChangesPresent(true)
    setPriorityAnchorEl(null)
  }

  const handleIPCChange = (isIPC) => {
    setDefect((prev) => ({
      ...prev,
      isIPC,
    }))
    setChangesPresent(true)
  }

  const handleStatusChange = (status) => {
    setDefect((prev) => ({
      ...prev,
      status,
    }))
    setChangesPresent(true)
    setStatusAnchorEl(null)
  }

  return (
    <DefectContext.Provider value={{ setChangesPresent, defect, setDefect }}>
      <Stack spacing={2.5} pb={4}>
        {loading && <LinearProgress sx={{ borderRadius: 99 }} />}

        {fetchError ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={loadDefect}>
                Try again
              </Button>
            }
          >
            Unable to fetch this defect. Please try again later.
          </Alert>
        ) : loading ? (
          <>
            <Skeleton variant="rounded" height={168} />
            <Grid container spacing={2}>
              <Grid item xs={12} lg={8}>
                <Skeleton variant="rounded" height={420} />
              </Grid>
              <Grid item xs={12} lg={4}>
                <Skeleton variant="rounded" height={420} />
              </Grid>
            </Grid>
          </>
        ) : (
          <>
            <Paper
              elevation={0}
              sx={{
                position: 'relative',
                overflow: 'hidden',
                p: { xs: 2, sm: 3 },
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 3,
                background:
                  'linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 62%, rgba(35,154,164,0.10) 100%)',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  width: 180,
                  height: 180,
                  borderRadius: '50%',
                  right: -70,
                  top: -100,
                  bgcolor: 'rgba(35,154,164,0.08)',
                },
              }}
            >
              <Stack spacing={2} position="relative" zIndex={1}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={1}
                >
                  <Button
                    variant="text"
                    color="secondary"
                    size="small"
                    startIcon={<ArrowBackRounded />}
                    onClick={() => navigate('/defects')}
                    sx={{ alignSelf: 'flex-start', px: 0.5 }}
                  >
                    Back to defects
                  </Button>
                  {defect?.defectNumber && (
                    <Chip label={defect.defectNumber} color="contrast" />
                  )}
                </Stack>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  justifyContent="space-between"
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Avatar
                      sx={{
                        width: 52,
                        height: 52,
                        bgcolor: 'contrast.main',
                        color: 'contrast.contrastText',
                        boxShadow: '0 8px 18px rgba(35,154,164,0.24)',
                      }}
                    >
                      <BuildCircleRounded />
                    </Avatar>
                    <Box minWidth={0}>
                      <Typography
                        variant="body2"
                        textTransform="uppercase"
                        letterSpacing="0.08em"
                        fontWeight={600}
                        fontSize="0.7rem"
                      >
                        Defect record
                      </Typography>
                      <Typography
                        variant="h4"
                        fontSize={{ xs: '1.35rem', sm: '1.7rem' }}
                        lineHeight={1.2}
                      >
                        {defect.summary?.trim() || 'Untitled defect'}
                      </Typography>
                    </Box>
                  </Stack>

                  <Stack
                    direction="row"
                    spacing={1}
                    flexWrap="wrap"
                    useFlexGap
                    alignItems="center"
                  >
                    <Chip
                      label={defect.status || 'New'}
                      color={getStatusColor(defect.status ?? '')}
                      sx={{ fontWeight: 600, cursor: 'pointer' }}
                      onClick={(e) => setStatusAnchorEl(e.currentTarget)}
                    />
                    <Menu
                      anchorEl={statusAnchorEl}
                      open={Boolean(statusAnchorEl)}
                      onClose={() => setStatusAnchorEl(null)}
                    >
                      {DEFECT_STATUSES.map((status) => (
                        <MenuItem
                          key={status}
                          selected={status === (defect.status || 'New')}
                          onClick={() => handleStatusChange(status)}
                        >
                          {status}
                        </MenuItem>
                      ))}
                    </Menu>
                    <Chip
                      label={
                        priorityLabels[defect.priority] || 'Normal priority'
                      }
                      variant="outlined"
                      color={defect.priority === 3 ? 'error' : 'default'}
                      onClick={(e) => setPriorityAnchorEl(e.currentTarget)}
                      sx={{ cursor: 'pointer' }}
                    />
                    <Menu
                      anchorEl={priorityAnchorEl}
                      open={Boolean(priorityAnchorEl)}
                      onClose={() => setPriorityAnchorEl(null)}
                    >
                      {DEFECT_PRIORITIES.map((priority) => (
                        <MenuItem
                          key={priority.value}
                          selected={priority.value === (defect.priority || 2)}
                          onClick={() => handlePriorityChange(priority.value)}
                        >
                          {priority.label}
                        </MenuItem>
                      ))}
                    </Menu>
                    <Tooltip title="IPC related">
                      <IconButton
                        checked={Boolean(defect.isIPC)}
                        onClick={() => handleIPCChange(!defect.isIPC)}
                        color={defect.isIPC ? 'error' : ''}
                      >
                        <Coronavirus fontSize="large" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
              </Stack>
            </Paper>
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12} lg={8}>
                  <Stack spacing={2}>
                    <Paper elevation={0} sx={surfaceSx}>
                      <Stack spacing={2}>
                        <SectionHeader
                          icon={<NotesRounded fontSize="small" />}
                          title="Defect details"
                          description="Update the summary and describe the issue clearly."
                        />
                        <Divider />
                        <Summary />
                        <Description />
                      </Stack>
                    </Paper>

                    <Paper elevation={0} sx={surfaceSx}>
                      <Stack spacing={2}>
                        <SectionHeader
                          icon={<BuildCircleRounded fontSize="small" />}
                          title="Attachments"
                          description="Photos and supporting images for this defect."
                        />
                        <Divider />
                        <Images />
                      </Stack>
                    </Paper>
                  </Stack>
                </Grid>

                <Grid item xs={12} lg={4}>
                  <Paper
                    elevation={0}
                    sx={{
                      ...surfaceSx,
                      height: '100%',
                      boxSizing: 'border-box',
                    }}
                  >
                    <Stack spacing={2}>
                      <SectionHeader
                        icon={<TuneRounded fontSize="small" />}
                        title="Management"
                        description="Status, priority, ownership and linked items."
                      />
                      <Divider />
                      <Box>
                        <Grid container spacing={1.25}>
                          {defect?.vdi?._id && (
                            <Grid item xs={12}>
                              <Button
                                variant="contained"
                                color="contrast"
                                onClick={() =>
                                  window.open(`/vdis/${defect.vdi._id}`)
                                }
                                fullWidth
                                size='small'
                              >
                                View VDI
                              </Button>
                            </Grid>
                          )}
                          <Grid item xs={12} lg={6}>
                            <DetailRow
                              noBorder
                              label="Reported"
                              value={
                                defect.createdAt
                                  ? moment(defect.createdAt).format(
                                      'Do MMMM YYYY',
                                    )
                                  : 'Not recorded'
                              }
                              avatar={false}
                            />
                          </Grid>
                          <Grid item xs={12} lg={6}>
                            <DetailRow
                              label="Reported by"
                              value={reporterName || 'Not recorded'}
                              avatar={defect?.reportedBy?.photograph}
                              noBorder
                            />
                          </Grid>
                        </Grid>
                      </Box>
                      <Box>
                        <Grid container spacing={1.25}>
                          <Grid item xs={12} sm={6} lg={12} xl={6}>
                            <DetailRow label="Status" noBorder>
                              <TextField
                                select
                                fullWidth
                                size="small"
                                value={defect.status || 'New'}
                                onChange={(e) =>
                                  handleStatusChange(e.target.value)
                                }
                                inputProps={{ 'aria-label': 'Status' }}
                              >
                                {DEFECT_STATUSES.map((status) => (
                                  <MenuItem key={status} value={status}>
                                    {status}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </DetailRow>
                          </Grid>
                          <Grid item xs={12} sm={6} lg={12} xl={6}>
                            <DetailRow label="Priority" noBorder>
                              <TextField
                                select
                                fullWidth
                                size="small"
                                value={defect.priority || 2}
                                onChange={(e) =>
                                  handlePriorityChange(Number(e.target.value))
                                }
                                inputProps={{ 'aria-label': 'Priority' }}
                              >
                                {DEFECT_PRIORITIES.map((priority) => (
                                  <MenuItem
                                    key={priority.value}
                                    value={priority.value}
                                  >
                                    {priority.label}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </DetailRow>
                          </Grid>
                          <Grid item xs={12}>
                            <DetailRow
                              label="Infection Prevention and Control"
                              noBorder
                            >
                              <Stack
                                component="label"
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                spacing={1}
                                sx={{ cursor: 'pointer' }}
                              >
                                <Typography variant="body2" fontWeight={600}>
                                  {defect.isIPC
                                    ? 'This defect affects IPC'
                                    : 'Not IPC related'}
                                </Typography>
                                <Switch
                                  color="error"
                                  checked={Boolean(defect.isIPC)}
                                  onChange={(e) =>
                                    handleIPCChange(e.target.checked)
                                  }
                                />
                              </Stack>
                            </DetailRow>
                          </Grid>
                        </Grid>
                      </Box>
                      <AssignedTo />
                      <Vehicle />
                      <Asset />
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
            <Paper elevation={0} sx={surfaceSx}>
              <Stack spacing={2}>
                <SectionHeader
                  icon={<ForumOutlined fontSize="small" />}
                  title="Activity & comments"
                  description="Updates and discussion linked to this defect."
                />
                <Divider />
                <LogsComponent
                  targetId={defect._id}
                  targetType="defect"
                  title="Comment"
                  allowCreateLog
                  setOpenComment={setOpenComment}
                  openComment={openComment}
                />
              </Stack>
            </Paper>
          </>
        )}
      </Stack>
      <UnsavedChanges
        changesPresent={changesPresent}
        handleSave={handleSave}
        handleReset={() => {
          loadDefect()
          setChangesPresent(false)
        }}
      />
    </DefectContext.Provider>
  )
}

export default SingleDefect
